import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { settingsApi } from "@/services/settings";
import { useCan } from "@/auth/permissions";

/**
 * CHANAV-3 · 「渠道余位计入酒店和门票」总开关（老板 / 技术管理员可改，默认关）。
 * 独立保存，不跟通用设置整包保存走；切换前页面内二次确认（切换会把全部已映射产品未来 180 天的余位重推一遍）。
 */
export function ChannelSeatsSwitchCard() {
  const qc = useQueryClient();
  const canEdit = useCan("system.settings.update");
  const q = useQuery({ queryKey: ["settings", "channel-seats"], queryFn: settingsApi.getChannelSeats });
  const [asking, setAsking] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: (on: boolean) => settingsApi.putChannelSeats(on),
    onSuccess: (r) => {
      setAsking(false);
      setDone(`已${r.include_hotel_ticket ? "打开" : "关闭"}，已把全部已映射产品未来 180 天的余位重推（${r.repushed_events ?? 0} 条）`);
      qc.invalidateQueries({ queryKey: ["settings", "channel-seats"] });
      qc.invalidateQueries({ queryKey: ["channel-center"] });
    },
  });
  const on = q.data?.include_hotel_ticket ?? false;

  return (
    <section data-testid="channel-seats-card" className="rounded-xl border border-slate-200 bg-white p-6">
      <h3 className="text-base font-semibold">📡 渠道余位</h3>
      <p className="mt-0.5 text-[12px] text-slate-500">GYG / Viator / Bokun / 携程 / 同行站 / 手机站对外报的余位怎么算 · 仅老板 + 技术管理员可改</p>

      <div className="mt-4 flex items-start justify-between gap-4">
        <div>
          <div className="text-[13.5px] font-semibold text-slate-900">渠道余位计入酒店和门票</div>
          <p className="mt-1 max-w-xl text-[12px] text-slate-500">
            打开前请确认酒店配额已录齐；没录的酒店晚不约束。关闭时余位只看团期名额和车位；打开后多日团还要看每晚酒店房数（按住房档）和公司要买的门票，避免平台上先显示有位、下单后又拒单。
          </p>
        </div>
        <button
          type="button" role="switch" aria-checked={on} aria-label="渠道余位计入酒店和门票" data-testid="channel-seats-switch"
          disabled={!canEdit || q.isLoading || mut.isPending}
          onClick={() => { setDone(null); setAsking(true); }}
          className={`h-6 w-11 shrink-0 rounded-full p-0.5 transition disabled:opacity-50 ${on ? "bg-blue-600" : "bg-slate-300"}`}
        >
          <span className={`block h-5 w-5 rounded-full bg-white transition ${on ? "translate-x-5" : ""}`} />
        </button>
      </div>

      {q.isError && <p className="mt-2 text-[12px] text-rose-600">开关状态加载失败，请刷新重试</p>}
      {!canEdit && <p className="mt-2 text-[12px] text-slate-400">只有老板 / 技术管理员能改这个开关</p>}

      {asking && (
        <div data-testid="channel-seats-confirm" className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="flex items-center gap-1.5 text-[12.5px] font-semibold text-amber-800"><AlertTriangle size={14} />
            确认{on ? "关闭" : "打开"}？
          </p>
          <p className="mt-1 text-[12px] text-amber-800">
            {on
              ? "关闭后渠道余位恢复成只看团期名额和车位，平台上的余位会重新推送一遍。"
              : "打开后渠道余位会计入酒店和门票（没录库存的酒店晚不约束），平台上的余位会按新口径重新推送一遍——酒店配额还没录齐的话，请先别开。"}
          </p>
          {mut.isError && <p role="alert" className="mt-1 text-[12px] text-rose-600">{(mut.error as Error)?.message || "保存失败，请重试"}</p>}
          <div className="mt-2 flex gap-2">
            <button type="button" onClick={() => setAsking(false)} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[12.5px] text-slate-700 hover:bg-slate-50">取消</button>
            <button type="button" data-testid="channel-seats-confirm-btn" disabled={mut.isPending} onClick={() => mut.mutate(!on)}
              className="rounded-lg bg-blue-600 px-3 py-1.5 text-[12.5px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
              {mut.isPending ? "保存中…" : `确认${on ? "关闭" : "打开"}`}
            </button>
          </div>
        </div>
      )}
      {done && <p data-testid="channel-seats-done" className="mt-3 rounded-lg bg-emerald-50 p-2.5 text-[12.5px] text-emerald-700">{done}</p>}
    </section>
  );
}
